import { useEffect, useRef } from "react";
import { collidingBalls } from "colliding_balls";
import Card from "./Card";
import Nav from "./Nav";
interface cardData {
  imgSrc: string;
  name: string;
  type: string;
  url: string;
  absPath?: boolean;
  newTab?: boolean;
}

function HomePage() {
  const canvasHostRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const hostElement = canvasHostRef.current;
    if (!hostElement) {
      return;
    }

    const instance = collidingBalls({
      $ele: hostElement,
      id: "collider",
      isStatic: false,
      count: 300,
      size: 10,
      speed: 4,
      sameSize: false,
      color: ["#7b5cff", "#25e4b2", "#9b5cff"],
      hollow: true,
      borderWidth: 1,
      gravity: 0.0,
      followCursor: true,
      cursorForce: 0.0015,
    });

    return () => {
      if (instance && typeof (instance as any).destroy === "function") {
        (instance as any).destroy();
      }
      const existingCanvas = document.getElementById("collider");
      if (existingCanvas && existingCanvas.parentElement === hostElement) {
        hostElement.removeChild(existingCanvas);
      }
    };
  }, []);

  const data: cardData[] = [
    {
      name: "Tahwul Dashboard",
      imgSrc: "./assets/tahwul.png",
      type: "React, TypeScript, Vite, Tailwind CSS",
      url: "https://alishah151.github.io/tahwuldashboard/",
      newTab: true,
    },
    {
      name: "Games",
      imgSrc: "./assets/arcadehub.png",
      type: "HTML, CSS, JS, PeerJS",
      url: "https://alishah151.github.io/games/",
      newTab: true,
    },
    {
      name: "Inventory Management Made Simple",
      imgSrc: "./assets/inventorysystem.png",
      type: "Vibe Coding, Next.js, React, TypeScript, TailwindCSS, Prisma ORM, NextAuth, bcrypt, Zod, Radix UI, Lucide Icons, Resend, Vitest, Playwright, ESLint",
      url: "https://inventory-system-flame-zeta.vercel.app/",
      newTab: true,
    },
    {
      name: "Swipe Animation Using Canvas",
      imgSrc: "./assets/swipe.png",
      type: "Canvas, Motion, WebGL, JS",
      url: "./swipe-api/index.html",
    },
    {
      name: "Interpolator",
      imgSrc: "./assets/interpolator.png",
      type: "Canvas, HTML, CSS, JS",
      url: "./interpolator//index.html",
    },
    {
      name: "spirograph",
      imgSrc: "./assets/spirograph.png",
      type: "Canvas, Math, JS",
      url: "./spirograph//index.html",
    },
    {
      name: "Salaries Calculation",
      imgSrc: "./assets/salaries.png",
      type: "Bootstrap, Forms, JS",
      url: "./salaries//index.html",
    },
    {
      name: "API Docs",
      imgSrc: "./assets/apidoc.png",
      type: "Docs, UI, JS",
      url: "./apidoc/index.html",
    },
    {
      name: "2048 (Use Arrow Keys)",
      imgSrc: "./assets/2048.png",
      type: "Game, SetJs",
      url: "https://alishah151.github.io/2048.github.io",
      absPath: true,
    },
    {
      name: "Notes",
      imgSrc: "./assets/notes.png",
      type: "Vue3, PWA",
      url: "./vue-notes/index.html",
    },
    {
      name: "Quiz",
      imgSrc: "./assets/quiz.png",
      type: "Vue3, Gamified",
      url: "./vue-quiz/index.html",
    },
    {
      name: "INSTA-micro-GRAM",
      imgSrc: "./assets/instagram.png",
      type: "Vue3, Antd, Realtime",
      url: "./vue-instagram/index.html",
    },
  ];

  const stack = [
    "React 18",
    "TypeScript",
    "Next.js",
    "Tailwind",
    "GSAP",
    "Three.js",
    "Vue 3",
    "Node.js",
    "Figma",
    "Firebase",
    "Jest",
  ];

  const stats = [
    {
      label: "Years shipping products",
      value: "13",
      detail: "since 2012 across remote teams",
    },
    {
      label: "Launches delivered",
      value: "25+",
      detail: "marketing sites, SaaS, desktop apps",
    },
    {
      label: "Teams partnered with",
      value: "10+",
      detail: "design, AI, data, growth squads",
    },
  ];

  return (
    <>
      <Nav />
      <main className="home" ref={canvasHostRef}>
        <section className="hero">
          <div className="hero-content">
            <p className="eyebrow">
              Frontend Developer · Human-centered interfaces
            </p>
            <h1>
              I build expressive digital experiences that feel bespoke, fast,
              and future-ready.
            </h1>
            <p className="hero-copy">
              From complex dashboards to motion-heavy marketing launches, I
              blend solid engineering, tasteful visuals, and UX craft to ship
              premium web experiences end-to-end.
            </p>
            <div className="hero-ctas">
              <a className="primary" href="#projects">
                Explore featured work
              </a>
              <a className="secondary" href="mailto:ali.eng.dev@gmail.com">
                Book a coffee chat
              </a>
            </div>
          </div>
          <div className="hero-panel">
            <div className="badge">Currently open for full-time</div>
            <h3>Recent builds</h3>
            <ul>
              {data.slice(0, 3).map((item) => (
                <li key={item.name}>
                  <span>{item.name}</span>
                  <span>→</span>
                </li>
              ))}
            </ul>
            <p>
              Every pixel engineered for clarity, delight, and measurable
              impact.
            </p>
          </div>
        </section>

        <section className="stats" aria-label="Impact metrics">
          {stats.map((stat) => (
            <div key={stat.label}>
              <p className="value">{stat.value}</p>
              <p className="label">{stat.label}</p>
              <p className="detail">{stat.detail}</p>
            </div>
          ))}
        </section>

        <section className="projects" id="projects">
          <header>
            <p className="eyebrow">Featured Projects</p>
            <h2>Selected experiments, products, and playgrounds.</h2>
            <p>
              A mix of production interfaces, rapid prototypes, and motion
              design studies showcasing my range across tooling, aesthetics, and
              interactivity.
            </p>
          </header>
          <div className="card-cont">
            {data.map((c: cardData, ind: number) => {
              return <Card {...c} key={ind} newTab={c.newTab} />;
            })}
          </div>
        </section>

        <section className="stack" id="stack">
          <header>
            <p className="eyebrow">Preferred Stack</p>
            <h2>Tools I reach for to ship polished products fast.</h2>
          </header>
          <div className="stack-grid">
            {stack.map((tool) => (
              <span key={tool}>{tool}</span>
            ))}
          </div>
        </section>

        <section className="cta">
          <h2>Need a frontend partner who cares about craft?</h2>
          <p>
            Let’s talk about your next launch, design system, or product
            iteration. I can jump in as a solo contributor or embed with your
            team.
          </p>
          <div className="hero-ctas">
            <a className="primary" href="mailto:ali.eng.dev@gmail.com">
              Start a project
            </a>
            <a
              className="secondary"
              href="https://alishah151.github.io/cv/"
              target="_blank"
              rel="noreferrer"
            >
              View Resume
            </a>
          </div>
        </section>
      </main>
    </>
  );
}

export default HomePage;
